/**
 * TimeWaver 主軸カテゴリ専用の発注 (仕入) ブロック (v3.1.8 / Phase C、2026-10 書類保管の再設計)。
 *
 * 既存の発注ブロック (DealFulfillmentTab) を差し替えるための専用コンポーネント。
 * 入金済み請求ごとの発注 (PO) カードに、ドイツ・DHL からの書類 3 種を届く順に並べる:
 *   ① プロフォーマインボイス (manufacturer_quotes。AI で番号・金額を読み取り → 送金依頼追加)
 *   ② 最終インボイス (PO.officialOrderPdfPath。旧称「正式発注書」)
 *   ③ DHL 輸入関税・関連書類 (PO.importDocuments)
 * どの欄も PDF を置いた時点で保存する (確認画面なし)。
 * 2026-10-30〜: その下に ④ 入荷・検品 / ⑤ 顧客へ発送 (PurchaseOrderFulfillmentPanel) を並べる。
 */
import type {
  ImportDocumentType,
  PurchaseOrder,
  PurchaseOrderImportDocument,
} from '@goenhub/shared';
import {
  IMPORT_DOCUMENT_TYPE_LABEL,
  PURCHASE_ORDER_STATUS_LABEL,
  currencySymbol,
  formatMoney,
} from '@goenhub/shared';
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  FileText,
  Link as LinkIcon,
  Loader2,
  Mail,
  Pencil,
  Plus,
  ShoppingCart,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { ManufacturerMailDialog } from './ManufacturerMailDialog';
import { PdfDropZone } from './PdfDropZone';
import { PurchaseOrderFulfillmentPanel } from './PurchaseOrderFulfillmentPanel';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useAdminDirectory } from '@/hooks/useAdminDirectory';
import {
  useClearOfficialOrderPdf,
  useConfirmPurchaseOrder,
  useDealManufacturerQuotes,
  useDeleteManufacturerQuote,
  usePdfDownloadUrl,
  useRemoveImportDocument,
  useUpdateImportDocumentType,
  useUpdateManufacturerQuote,
  useUpdateManufacturerQuoteInvoiceLink,
  useUploadImportDocuments,
  useUploadManufacturerQuote,
  useUploadOfficialOrderPdf,
  type ManufacturerQuoteRecord,
  type ManufacturerMailKind,
} from '@/hooks/useManufacturerQuotes';
import { useDealInvoices } from '@/hooks/useSalesOps';
import {
  useAddTransferRequest,
  type AccountingChannelOutcome,
  type AccountingNotifyOutcome,
} from '@/hooks/useTransferRequests';
import { PO_STATUS_VARIANT } from '@/lib/purchaseOrderStatus';
import { paths } from '@/routes/paths';

type PurchaseOrderRow = PurchaseOrder & { id: string };
/** uid → 表示名。ディレクトリ取得前・取得失敗時は空文字。 */
type NameOf = (uid: string | null | undefined) => string;

/**
 * 請求書セレクタ用のオプション順を整える。
 * 未入金 (status !== 'paid') を新しい順 → その他を新しい順。
 */
function sortInvoicesForSelector<T extends { status?: string; issuedAt?: unknown }>(
  invoices: readonly T[],
): T[] {
  const unpaid: T[] = [];
  const others: T[] = [];
  for (const iv of invoices) {
    if (iv.status === 'paid') others.push(iv);
    else unpaid.push(iv);
  }
  return [...unpaid, ...others];
}

interface Props {
  dealId: string;
  /** 既存の onInvoicePaid Trigger で起票された PO 一覧 */
  purchaseOrders: PurchaseOrderRow[];
  /** 顧客名 (送金申請メールの件名生成用) */
  contactName?: string | null;
  /**
   * メーカー (発注先) メールアドレス (v3.2.28+)。
   * 主軸カテゴリーの設定から取得。null のときは「メーカー宛通知」ボタンを無効化。
   */
  manufacturerEmail?: string | null;
  /** メーカー取引通貨 ISO コード (カテゴリー設定より)。既定 EUR。 */
  manufacturerCurrency?: string | null;
  /** この発注カードまでスクロールして強調する (発注一覧・次のアクション等から開いた時)。 */
  focusPoId?: string | null;
  /** 同じ発注への再フォーカスを区別する値 (変わるたびにスクロールし直す)。 */
  focusNonce?: number;
}

export function TimeWaverPurchaseOrderBlock({
  dealId,
  purchaseOrders,
  contactName,
  manufacturerEmail,
  manufacturerCurrency,
  focusPoId,
  focusNonce,
}: Props) {
  const currency = manufacturerCurrency || 'EUR';
  const mqs = useDealManufacturerQuotes(dealId);
  const invoicesQuery = useDealInvoices(dealId);
  const directory = useAdminDirectory();

  const nameOf = useMemo<NameOf>(() => {
    const m = new Map<string, string>();
    for (const a of directory.data ?? []) m.set(a.uid, a.displayName || a.fullName || '');
    return (uid) => (uid ? (m.get(uid) ?? '') : '');
  }, [directory.data]);

  // invoiceId → invoice の逆引きマップ
  const invById = useMemo(() => {
    const m = new Map<string, { invoiceNumber?: string; amount?: number; status?: string }>();
    for (const iv of invoicesQuery.data ?? []) m.set(iv.id, iv);
    return m;
  }, [invoicesQuery.data]);

  // 入金済み請求ごとの PO を、対応請求番号の新しい順で並べる
  const sortedPos = useMemo(() => {
    return [...purchaseOrders].sort((a, b) => {
      const na = (a.invoiceId ? invById.get(a.invoiceId)?.invoiceNumber : '') ?? '';
      const nb = (b.invoiceId ? invById.get(b.invoiceId)?.invoiceNumber : '') ?? '';
      return nb.localeCompare(na);
    });
  }, [purchaseOrders, invById]);

  // どの PO にも属さないメーカー見積 (invoiceId 無し or 対応 PO 無し) は末尾にまとめる
  const poInvoiceIds = useMemo(
    () => new Set(purchaseOrders.map((p) => p.invoiceId).filter(Boolean) as string[]),
    [purchaseOrders],
  );
  const orphanMqs = (mqs.data ?? []).filter(
    (mq) => !mq.invoiceId || !poInvoiceIds.has(mq.invoiceId),
  );

  const loading = mqs.isLoading || invoicesQuery.isLoading;

  return (
    <div className="space-y-3">
      <div>
        <h4 className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider">
          TimeWaver 発注書類
        </h4>
        <p className="text-[11px] text-[var(--text-muted)] mt-0.5">
          入金確認済みの請求ごとに発注カードが自動作成されます。届いた書類は、該当する欄に PDF
          を置くと保存されます。
        </p>
      </div>

      {loading ? (
        <Spinner />
      ) : sortedPos.length === 0 ? (
        <p className="text-[11px] text-[var(--text-secondary)] rounded-md border border-dashed border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2">
          発注 (PO) は請求の入金確認 (paid) 時に自動作成されます。
        </p>
      ) : (
        <div className="space-y-3">
          {sortedPos.map((po) => (
            <PurchaseOrderCard
              key={po.id}
              dealId={dealId}
              po={po}
              invoice={po.invoiceId ? (invById.get(po.invoiceId) ?? null) : null}
              mqs={(mqs.data ?? []).filter((mq) => !!mq.invoiceId && mq.invoiceId === po.invoiceId)}
              contactName={contactName ?? null}
              manufacturerEmail={manufacturerEmail ?? null}
              currency={currency}
              nameOf={nameOf}
              focused={!!focusPoId && focusPoId === po.id}
              focusNonce={focusNonce ?? 0}
            />
          ))}

          {orphanMqs.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">
                どの発注にも紐付いていないプロフォーマインボイス
              </p>
              {orphanMqs.map((mq) => (
                <ManufacturerQuoteCard
                  key={mq.id}
                  dealId={dealId}
                  mq={mq}
                  linkedPo={purchaseOrders.find((p) => p.manufacturerQuoteId === mq.id) ?? null}
                  contactName={contactName ?? null}
                  manufacturerEmail={manufacturerEmail ?? null}
                  currency={currency}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// 入金済み請求 (=PO) 1 件のカード。対応請求を明記し、上部に書類の進み具合、
// その下に ① プロフォーマ / ② 最終インボイス / ③ DHL 書類 の欄を並べる。
// ─────────────────────────────────────────────
function PurchaseOrderCard({
  dealId,
  po,
  invoice,
  mqs,
  contactName,
  manufacturerEmail,
  currency,
  nameOf,
  focused,
  focusNonce,
}: {
  dealId: string;
  po: PurchaseOrderRow;
  invoice: { invoiceNumber?: string; amount?: number; status?: string } | null;
  mqs: ManufacturerQuoteRecord[];
  contactName: string | null;
  manufacturerEmail: string | null;
  currency: string;
  nameOf: NameOf;
  focused: boolean;
  focusNonce: number;
}) {
  const [orderMailOpen, setOrderMailOpen] = useState(false);
  const [highlight, setHighlight] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const pfRef = useRef<HTMLDivElement>(null);
  const finRef = useRef<HTMLDivElement>(null);
  const dhlRef = useRef<HTMLDivElement>(null);
  const fulfillRef = useRef<HTMLDivElement>(null);
  const upload = useUploadManufacturerQuote();
  // 送信〜AI 読み取り完了までの間だけ表示する仮カード
  const [pending, setPending] = useState<Array<{ key: string; fileName: string }>>([]);

  // 発注一覧・次のアクション等から開かれた時は、このカードまでスクロールして一時的に強調する
  useEffect(() => {
    if (!focused) return;
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setHighlight(true);
    const t = setTimeout(() => setHighlight(false), 2600);
    return () => clearTimeout(t);
  }, [focused, focusNonce]);

  const statusLabel = PURCHASE_ORDER_STATUS_LABEL[po.status] ?? String(po.status ?? '');

  const importDocs = po.importDocuments ?? [];
  const steps = [
    { label: '① プロフォーマ', done: mqs.length > 0, ref: pfRef },
    { label: '② 最終インボイス', done: !!po.officialOrderPdfPath, ref: finRef },
    { label: `③ DHL書類 ${importDocs.length}件`, done: importDocs.length > 0, ref: dhlRef },
    {
      label: '④ 入荷・検品',
      done: po.status === 'inspected' || po.status === 'shipped',
      ref: fulfillRef,
    },
    { label: '⑤ 顧客へ発送', done: po.status === 'shipped', ref: fulfillRef },
  ];

  const handleProformaFiles = async (files: File[]) => {
    for (const file of files) {
      const key = `${Date.now()}-${file.name}`;
      setPending((p) => [...p, { key, fileName: file.name }]);
      try {
        const base64 = await readFileAsBase64(file);
        const r = await upload.mutateAsync({
          dealId,
          fileName: file.name,
          contentBase64: base64,
          contentType: file.type,
          invoiceId: po.invoiceId ?? null,
        });
        if (r.extractionError) {
          toast.warning(
            `プロフォーマインボイスを保存しました（番号と金額は読み取れませんでした: ${r.extractionError}）。手動で入力してください`,
          );
        } else {
          toast.success(
            `プロフォーマインボイスを保存しました。読み取り: ${r.invoiceNumber ?? '-'} / ${formatMoney(r.totalAmount ?? 0, currency)}`,
          );
        }
      } catch (err) {
        toast.error(`保存に失敗しました: ${(err as Error).message}`);
      } finally {
        setPending((p) => p.filter((x) => x.key !== key));
      }
    }
  };

  return (
    <div
      ref={cardRef}
      className={`rounded-md border border-[var(--border)] bg-[var(--bg-surface)] overflow-hidden scroll-mt-4 transition-shadow ${
        highlight ? 'ring-2 ring-[var(--accent)] shadow-[0_0_24px_rgba(74,124,255,0.35)]' : ''
      }`}
    >
      {/* PO ヘッダ: PO番号 + 対応請求 + ステータス + 発注メール */}
      <div className="flex items-start justify-between gap-2 p-3 border-b border-[var(--border)] bg-[var(--bg-elevated)]">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <ShoppingCart className="size-3.5 text-[var(--text-muted)] shrink-0" />
            <span className="font-mono text-xs font-semibold text-[var(--text-primary)]">
              {po.poNumber}
            </span>
            <Badge variant={PO_STATUS_VARIANT[po.status] ?? 'default'}>{statusLabel}</Badge>
            {po.manufacturerQuoteRequestedAt && (
              <Badge variant="sent">
                <Mail className="size-2.5 mr-0.5" />
                見積依頼済み
              </Badge>
            )}
          </div>
          <p className="text-[10px] text-[var(--text-muted)] mt-1">
            対応請求:{' '}
            {invoice ? (
              <span className="font-mono text-[var(--text-secondary)]">
                {invoice.invoiceNumber ?? po.invoiceId} · ¥{(invoice.amount ?? 0).toLocaleString()}
              </span>
            ) : (
              <span className="text-[var(--accent-amber)]">請求未紐付け</span>
            )}
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          className="shrink-0"
          onClick={() => setOrderMailOpen(true)}
          title="この発注についてメーカーへ見積依頼メールを送信します"
        >
          <Mail className="size-3.5" />
          メーカーへ発注メール
        </Button>
      </div>

      {/* 書類の進み具合。押すとその欄へ移動 */}
      <div className="flex flex-wrap gap-2 p-3 border-b border-[var(--border)]">
        {steps.map((s) => (
          <button
            key={s.label}
            type="button"
            onClick={() => s.ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            className={`flex-1 min-w-[130px] inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[11px] text-left bg-[var(--bg-elevated)] hover:border-[var(--accent)] transition-colors ${
              s.done
                ? 'border-[var(--accent-green)] text-[var(--text-primary)]'
                : 'border-[var(--border)] text-[var(--text-secondary)]'
            }`}
          >
            <Badge variant={s.done ? 'approved' : 'draft'}>{s.done ? '✓' : '－'}</Badge>
            {s.label}
          </button>
        ))}
      </div>

      {/* ① プロフォーマインボイス */}
      <div ref={pfRef} className="p-3 space-y-2.5 scroll-mt-4">
        <p className="text-xs font-semibold text-[var(--text-primary)]">① プロフォーマインボイス</p>
        {mqs.length === 0 && pending.length === 0 && (
          <p className="text-[11px] text-[var(--text-muted)]">
            「メーカーへ発注メール」で見積依頼を送り、届いたプロフォーマインボイスをここに置きます。
          </p>
        )}
        {mqs.map((mq) => (
          <ManufacturerQuoteCard
            key={mq.id}
            dealId={dealId}
            mq={mq}
            linkedPo={po}
            contactName={contactName}
            manufacturerEmail={manufacturerEmail}
            currency={currency}
          />
        ))}
        {pending.map((p) => (
          <div
            key={p.key}
            className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 flex items-center gap-2 flex-wrap"
          >
            <FileText className="size-3.5 text-[var(--text-muted)] shrink-0" />
            <span className="text-xs font-medium truncate max-w-full">{p.fileName}</span>
            <span className="inline-flex items-center gap-1 text-[11px] text-[var(--accent)]">
              <Loader2 className="size-3 animate-spin" />
              保存して番号と金額を読み取り中…（このまま他の操作ができます）
            </span>
          </div>
        ))}
        <PdfDropZone
          label={
            mqs.length > 0 || pending.length > 0
              ? '別のプロフォーマインボイスをここにドロップ ／ クリックして選択'
              : 'プロフォーマインボイスの PDF をここにドロップ ／ クリックして選択'
          }
          onFiles={handleProformaFiles}
        />
      </div>

      {/* ② 最終インボイス */}
      <div ref={finRef} className="p-3 space-y-2.5 border-t border-[var(--border)] scroll-mt-4">
        <FinalInvoiceSection dealId={dealId} po={po} currency={currency} nameOf={nameOf} />
      </div>

      {/* ③ DHL 輸入関税・関連書類 */}
      <div ref={dhlRef} className="p-3 space-y-2.5 border-t border-[var(--border)] scroll-mt-4">
        <ImportDocumentsSection dealId={dealId} po={po} nameOf={nameOf} />
      </div>

      {/* ④ 入荷・検品 → ⑤ 顧客へ発送 (2026-10-30〜) */}
      <div
        ref={fulfillRef}
        className="p-3 space-y-2.5 border-t border-[var(--border)] scroll-mt-4 bg-[var(--bg-elevated)]"
      >
        <p className="text-xs font-semibold text-[var(--text-primary)]">
          ④ 入荷・検品 → ⑤ 顧客へ発送
        </p>
        <PurchaseOrderFulfillmentPanel
          dealId={dealId}
          po={po}
          isTimeWaver
          contactName={contactName}
          showSteps={false}
        />
      </div>

      {/* この発注専用のメーカー宛「見積依頼」メール送信モーダル */}
      <ManufacturerMailDialog
        open={orderMailOpen}
        onOpenChange={setOrderMailOpen}
        dealId={dealId}
        kind="quote_request"
        manufacturerQuoteId={null}
        poId={po.id}
      />
    </div>
  );
}

// ─────────────────────────────────────────────
// プロフォーマインボイス 1 件のカード (AI 読み取り値の確認・修正 → 送金依頼)
// ─────────────────────────────────────────────

function ManufacturerQuoteCard({
  dealId,
  mq,
  linkedPo,
  currency,
}: {
  dealId: string;
  mq: ManufacturerQuoteRecord;
  linkedPo: PurchaseOrderRow | null;
  contactName: string | null;
  /** 主軸カテゴリーから渡される発注先メール（モーダル側で自動解決するため未使用）。 */
  manufacturerEmail?: string | null;
  /** メーカー取引通貨 ISO コード。金額ラベル表示に使う。 */
  currency: string;
}) {
  const curSym = currencySymbol(currency);
  const update = useUpdateManufacturerQuote();
  const deleteFn = useDeleteManufacturerQuote();
  const confirmPo = useConfirmPurchaseOrder();
  const updateLink = useUpdateManufacturerQuoteInvoiceLink();
  // メーカー宛「見積依頼」メール送信モーダル
  const [mailKind, setMailKind] = useState<ManufacturerMailKind | null>(null);
  // 送金依頼追加 (経理 > 送金依頼 に並べる + 設定に応じて経理へ通知)
  const addTransfer = useAddTransferRequest();
  const pdfUrl = usePdfDownloadUrl(mq.pdfStoragePath);
  const invoicesQuery = useDealInvoices(dealId);
  const linkedInvoice = useMemo(
    () => (mq.invoiceId ? invoicesQuery.data?.find((iv) => iv.id === mq.invoiceId) : null),
    [mq.invoiceId, invoicesQuery.data],
  );
  const sortedInvoices = useMemo(
    () => sortInvoicesForSelector(invoicesQuery.data ?? []),
    [invoicesQuery.data],
  );
  const [linkEditOpen, setLinkEditOpen] = useState(false);
  const [linkDraft, setLinkDraft] = useState<string>(mq.invoiceId ?? '');

  useEffect(() => {
    setLinkDraft(mq.invoiceId ?? '');
  }, [mq.invoiceId]);

  const handleSaveLink = async () => {
    try {
      await updateLink.mutateAsync({
        dealId,
        manufacturerQuoteId: mq.id,
        invoiceId: linkDraft || null,
      });
      toast.success('紐付け請求を更新しました');
      setLinkEditOpen(false);
    } catch (err) {
      toast.error(`更新失敗: ${(err as Error).message}`);
    }
  };

  const [invoiceNumber, setInvoiceNumber] = useState(mq.invoiceNumber ?? '');
  const [totalAmount, setTotalAmount] = useState(
    mq.totalAmount != null ? String(mq.totalAmount) : '',
  );
  const [discountAmount, setDiscountAmount] = useState(String(mq.discountAmount ?? 0));

  // 親レコードが更新されたらフォームに反映 (AI 抽出完了時など)
  useEffect(() => {
    setInvoiceNumber(mq.invoiceNumber ?? '');
    setTotalAmount(mq.totalAmount != null ? String(mq.totalAmount) : '');
    setDiscountAmount(String(mq.discountAmount ?? 0));
  }, [mq.invoiceNumber, mq.totalAmount, mq.discountAmount]);

  const isProcessed = mq.status === 'processed';
  // 読み取り途中で画面を開き直した場合 (レコードはあるが抽出結果がまだ無い)
  const isReading =
    !isProcessed &&
    !mq.extractedAt &&
    !mq.extractionError &&
    mq.invoiceNumber == null &&
    mq.totalAmount == null;
  // 送金依頼に使われたプロフォーマ。旧データで manufacturerQuoteId が無い PO は従来どおり全件扱い
  const isAdopted = !linkedPo?.manufacturerQuoteId || linkedPo.manufacturerQuoteId === mq.id;
  const transferRequested = !!linkedPo?.transferRequestedAt;
  const totalNum = Number(totalAmount.replace(/[^\d.-]/g, '')) || 0;
  const discountNum = Number(discountAmount.replace(/[^\d.-]/g, '')) || 0;
  const finalAmount = Math.max(0, totalNum - discountNum);

  const handleSaveEdits = async () => {
    try {
      await update.mutateAsync({
        dealId,
        manufacturerQuoteId: mq.id,
        invoiceNumber: invoiceNumber.trim() || null,
        totalAmount: totalNum || null,
        discountAmount: discountNum,
      });
      toast.success('プロフォーマインボイスの内容を更新しました');
    } catch (err) {
      toast.error(`保存失敗: ${(err as Error).message}`);
    }
  };

  const handleDelete = async () => {
    if (
      !confirm(
        'このプロフォーマインボイスの登録を削除しますか？（PDF ファイル自体は保管場所に残ります）',
      )
    )
      return;
    try {
      await deleteFn.mutateAsync({
        dealId,
        manufacturerQuoteId: mq.id,
      });
      toast.success('削除しました');
    } catch (err) {
      toast.error(`削除失敗: ${(err as Error).message}`);
    }
  };

  // 送金依頼追加: 未確定なら見積金額を PO に確定 (mq → PO) してから、経理 > 送金依頼 に追加する。
  // 経理担当への通知は 営業・通知設定 > 自動通知「新たな送金依頼を追加したとき」に従ってサーバーが送る。
  const handleRequestTransfer = async () => {
    if (!totalNum) {
      toast.error('合計金額を入力してください');
      return;
    }
    if (!linkedPo) return;
    if (
      !confirm(
        `送金依頼に追加します（送金額 ${formatMoney(finalAmount, currency)}）。\n経理 > 送金依頼 に表示され、設定に応じて経理担当へ通知されます。よろしいですか？`,
      )
    )
      return;
    try {
      if (!isProcessed) {
        // 編集中の値があれば先に保存
        if (
          invoiceNumber.trim() !== (mq.invoiceNumber ?? '') ||
          totalNum !== (mq.totalAmount ?? 0) ||
          discountNum !== (mq.discountAmount ?? 0)
        ) {
          await update.mutateAsync({
            dealId,
            manufacturerQuoteId: mq.id,
            invoiceNumber: invoiceNumber.trim() || null,
            totalAmount: totalNum,
            discountAmount: discountNum,
          });
        }
        // PO に金額を確定（送金額の算出元）。メーカーへの発注メールは送らない。
        await confirmPo.mutateAsync({
          dealId,
          manufacturerQuoteId: mq.id,
          discountAmount: discountNum,
        });
      }
      const r = await addTransfer.mutateAsync({
        dealId,
        poId: linkedPo.id,
        manufacturerQuoteId: mq.id,
      });
      if (r.alreadyExisted || !r.notify) {
        toast.info('この発注は既に送金依頼に追加されています（金額などを最新にしました）');
        return;
      }
      showAddResultToast(r.notify);
    } catch (err) {
      toast.error(`送金依頼の追加に失敗: ${(err as Error).message}`);
    }
  };

  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
          <FileText className="size-3.5 text-[var(--text-muted)] shrink-0" />
          <span className="text-xs font-medium truncate">{mq.originalFileName}</span>
          {isReading && (
            <span className="inline-flex items-center gap-1 text-[10px] text-[var(--accent)]">
              <Loader2 className="size-3 animate-spin" />
              番号と金額を読み取り中…
            </span>
          )}
          {mq.extractionError && !isProcessed && (
            <span className="inline-flex items-center gap-1 text-[10px] text-[var(--accent-amber)]">
              <AlertTriangle className="size-3" />
              読み取れませんでした（手動で入力）
            </span>
          )}
          {!mq.extractionError && mq.extractedAt && !isProcessed && (
            <span className="inline-flex items-center gap-1 text-[10px] text-[var(--accent-green)]">
              <Sparkles className="size-3" />
              読み取り完了
            </span>
          )}
          {isProcessed && <Badge variant="approved">発注済み</Badge>}
        </div>
        {!isProcessed && (
          <button
            type="button"
            onClick={handleDelete}
            className="p-1 text-[var(--text-muted)] hover:text-[var(--accent-red)]"
            title="登録を削除"
            aria-label="プロフォーマインボイスの登録を削除"
          >
            <Trash2 className="size-3.5" />
          </button>
        )}
      </div>

      {/* 紐付け請求書 表示 + 編集 */}
      <div className="flex items-center justify-between gap-2 -mt-1">
        {mq.invoiceId ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-[var(--text-secondary)]">
            <LinkIcon className="size-3" />
            紐付け請求:
            <span className="font-mono font-semibold text-[var(--accent)]">
              {linkedInvoice?.invoiceNumber ?? mq.invoiceId.slice(0, 8)}
            </span>
            {linkedInvoice && (
              <span className="text-[10px] text-[var(--text-muted)]">
                · {linkedInvoice.status === 'paid' ? '入金済' : '未入金'}
              </span>
            )}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] text-[var(--accent-amber)]">
            <AlertTriangle className="size-3" />
            請求未紐付け
          </span>
        )}
        <button
          type="button"
          onClick={() => setLinkEditOpen(true)}
          className="text-[10px] text-[var(--accent)] hover:underline inline-flex items-center gap-0.5"
        >
          <Pencil className="size-3" />
          請求変更
        </button>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-2">
        <div>
          <Label className="text-[10px]">インボイス番号</Label>
          <Input
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            disabled={isProcessed}
            className="h-8 text-xs"
          />
        </div>
        <div>
          <Label className="text-[10px]">合計金額 ({curSym.trim()})</Label>
          <Input
            type="text"
            inputMode="decimal"
            value={totalAmount}
            onChange={(e) => setTotalAmount(e.target.value)}
            disabled={isProcessed}
            className="h-8 text-xs tabular-nums"
          />
        </div>
        <div>
          <Label className="text-[10px]">割引額 ({curSym.trim()})</Label>
          <Input
            type="text"
            inputMode="decimal"
            value={discountAmount}
            onChange={(e) => setDiscountAmount(e.target.value)}
            disabled={isProcessed}
            className="h-8 text-xs tabular-nums"
            placeholder="0"
          />
        </div>
        <div>
          <Label className="text-[10px]">送金額 ({curSym.trim()})</Label>
          <div className="h-8 px-2 flex items-center text-xs font-semibold tabular-nums text-[var(--accent)]">
            {formatMoney(finalAmount, currency)}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 pt-1 items-center">
        {pdfUrl.data && (
          <Button variant="secondary" size="sm" onClick={() => window.open(pdfUrl.data, '_blank')}>
            <ExternalLink className="size-3" />
            開く
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setMailKind('quote_request')}
          title="メーカーへ見積依頼メールを再送信（編集して送信）"
        >
          <Mail className="size-3" />
          見積依頼メール
        </Button>
        {!isProcessed && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSaveEdits}
            disabled={update.isPending}
          >
            {update.isPending ? '保存中…' : '編集を保存'}
          </Button>
        )}
        {/* 送金依頼追加: 経理 > 送金依頼 に追加する。追加後は採用したプロフォーマにだけ「送金依頼に追加済み」を出す */}
        {transferRequested ? (
          isAdopted && (
            <>
              <Badge variant="approved">送金依頼に追加済み</Badge>
              <Link
                to={`${paths.financeHub}?hub=transfers`}
                className="text-[11px] text-[var(--accent)] hover:underline"
              >
                経理 &gt; 送金依頼 を開く
              </Link>
            </>
          )
        ) : (
          <Button
            variant="primary"
            size="sm"
            onClick={handleRequestTransfer}
            disabled={confirmPo.isPending || addTransfer.isPending || !totalNum || !linkedPo}
            title="経理 > 送金依頼 に追加します"
          >
            <Plus className="size-3" />
            {confirmPo.isPending || addTransfer.isPending ? '処理中…' : '送金依頼追加'}
          </Button>
        )}
      </div>

      {/* メーカー宛「見積依頼」メール送信モーダル */}
      <ManufacturerMailDialog
        open={mailKind !== null}
        onOpenChange={(o) => !o && setMailKind(null)}
        dealId={dealId}
        kind={mailKind ?? 'quote_request'}
        manufacturerQuoteId={mq.id}
        poId={linkedPo?.id ?? null}
      />

      {/* ── 紐付け請求 編集ダイアログ ── */}
      <Dialog open={linkEditOpen} onOpenChange={setLinkEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>紐付け請求書を変更</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px]">紐付ける請求書</Label>
              <select
                value={linkDraft}
                onChange={(e) => setLinkDraft(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
              >
                <option value="">未紐付け（推奨しません）</option>
                {sortedInvoices.map((iv) => {
                  const label = `${iv.invoiceNumber ?? iv.id.slice(0, 8)} · ¥${(iv.amount ?? 0).toLocaleString()} · ${iv.status === 'paid' ? '入金済' : '未入金'}`;
                  return (
                    <option key={iv.id} value={iv.id}>
                      {label}
                    </option>
                  );
                })}
              </select>
              {linkDraft === '' && (
                <p className="text-[10px] text-[var(--accent-amber)] mt-1 inline-flex items-center gap-1">
                  <AlertTriangle className="size-3" />
                  どの請求の発注か追跡できません。
                </p>
              )}
            </div>
            <p className="text-[10px] text-[var(--text-muted)]">
              変更内容は商談ログ（Activity）に記録されます。
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setLinkEditOpen(false)}
              disabled={updateLink.isPending}
            >
              キャンセル
            </Button>
            <Button variant="primary" onClick={handleSaveLink} disabled={updateLink.isPending}>
              {updateLink.isPending ? '保存中…' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────
// ② 最終インボイス (発注カードに 1 つ。送金依頼の前から表示し、差し替え・登録解除ができる)
// ─────────────────────────────────────────────

function FinalInvoiceSection({
  dealId,
  po,
  currency,
  nameOf,
}: {
  dealId: string;
  po: PurchaseOrderRow;
  currency: string;
  nameOf: NameOf;
}) {
  const upload = useUploadOfficialOrderPdf();
  const clear = useClearOfficialOrderPdf();
  const pdfUrl = usePdfDownloadUrl(po.officialOrderPdfPath ?? null);
  const replaceInputRef = useRef<HTMLInputElement>(null);

  const totalAmount = po.manufacturerTotalAmount ?? 0;
  const discountAmount = po.discountAmount ?? 0;
  const finalAmount = totalAmount - discountAmount;
  const fileName =
    po.officialOrderFileName ?? fileNameFromStoragePath(po.officialOrderPdfPath ?? '');
  const uploader = nameOf(po.officialOrderUploadedBy);

  const save = async (file: File, replacing: boolean) => {
    try {
      await upload.mutateAsync({ dealId, poId: po.id, file });
      toast.success(replacing ? '最終インボイスを差し替えました' : '最終インボイスを保存しました');
    } catch (err) {
      toast.error(`保存に失敗しました: ${(err as Error).message}`);
    }
  };

  const handleReplacePicked = (list: FileList | null) => {
    const f = list?.[0];
    if (!f) return;
    if (f.type !== 'application/pdf') {
      toast.error('PDF を選択してください');
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error('20MB 以下にしてください');
      return;
    }
    void save(f, true);
  };

  const handleClear = async () => {
    if (!confirm('最終インボイスの登録を外しますか？（PDF ファイル自体は保管場所に残ります）'))
      return;
    try {
      await clear.mutateAsync({ dealId, poId: po.id });
      toast.success('最終インボイスの登録を外しました');
    } catch (err) {
      toast.error(`失敗しました: ${(err as Error).message}`);
    }
  };

  return (
    <>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-xs font-semibold text-[var(--text-primary)]">② 最終インボイス</span>
        <span className="text-[11px] text-[var(--text-muted)]">通常は送金依頼の後に届きます</span>
      </div>

      {po.officialOrderPdfPath ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] px-2.5 py-2">
          <Badge variant="approved">
            <CheckCircle2 className="size-2.5 mr-0.5" />
            保存済み
          </Badge>
          <span className="flex-1 min-w-[140px] truncate font-mono text-xs">{fileName}</span>
          <span className="text-[10px] text-[var(--text-muted)]">
            {fmtDay(po.officialOrderUploadedAt)}
            {uploader && ` · ${uploader}`}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => pdfUrl.data && window.open(pdfUrl.data, '_blank')}
            disabled={!pdfUrl.data}
          >
            <ExternalLink className="size-3" />
            開く
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => replaceInputRef.current?.click()}
            disabled={upload.isPending}
          >
            {upload.isPending ? '保存中…' : '差し替え'}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleClear} disabled={clear.isPending}>
            削除
          </Button>
          <input
            ref={replaceInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => {
              handleReplacePicked(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      ) : upload.isPending ? (
        <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-3 text-[11px] text-[var(--accent)] inline-flex items-center gap-1.5 w-full">
          <Loader2 className="size-3 animate-spin" />
          最終インボイスを保存しています…
        </div>
      ) : (
        <PdfDropZone
          label="最終インボイスの PDF をここにドロップ ／ クリックして選択"
          onFiles={(files) => void save(files[0], false)}
        />
      )}

      {po.transferRequestedAt && (
        <div className="grid grid-cols-3 gap-2 text-[11px]">
          <div>
            <div className="text-[10px] text-[var(--text-muted)]">合計</div>
            <div className="tabular-nums">{formatMoney(totalAmount, currency)}</div>
          </div>
          <div>
            <div className="text-[10px] text-[var(--text-muted)]">割引</div>
            <div className="tabular-nums">{formatMoney(discountAmount, currency)}</div>
          </div>
          <div>
            <div className="text-[10px] text-[var(--text-muted)]">送金額</div>
            <div className="tabular-nums font-semibold text-[var(--accent)]">
              {formatMoney(finalAmount, currency)}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────
// ③ DHL 輸入関税・関連書類 (複数可。置いた書類は「輸入関税書類」で登録し、行で種類を切り替える)
// ─────────────────────────────────────────────

function ImportDocumentsSection({
  dealId,
  po,
  nameOf,
}: {
  dealId: string;
  po: PurchaseOrderRow;
  nameOf: NameOf;
}) {
  const upload = useUploadImportDocuments();
  const docs = po.importDocuments ?? [];
  const [uploadingCount, setUploadingCount] = useState(0);

  const handleFiles = async (files: File[]) => {
    setUploadingCount(files.length);
    try {
      const r = await upload.mutateAsync({ dealId, poId: po.id, files, type: 'import_duty' });
      toast.success(`DHL 書類を ${r.count} 件保存しました（種類: 輸入関税書類）`);
    } catch (err) {
      toast.error(`保存に失敗しました: ${(err as Error).message}`);
    } finally {
      setUploadingCount(0);
    }
  };

  return (
    <>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-xs font-semibold text-[var(--text-primary)]">
          ③ DHL 輸入関税・関連書類
        </span>
        <span className="text-[11px] text-[var(--text-muted)]">{docs.length}件</span>
      </div>
      {docs.map((d) => (
        <ImportDocumentRow key={d.id} dealId={dealId} poId={po.id} doc={d} nameOf={nameOf} />
      ))}
      {uploadingCount > 0 && (
        <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2 text-[11px] text-[var(--accent)] inline-flex items-center gap-1.5 w-full">
          <Loader2 className="size-3 animate-spin" />
          {uploadingCount} 件を保存しています…
        </div>
      )}
      <PdfDropZone
        multiple
        disabled={upload.isPending}
        label="DHL の書類をここにドロップ（複数可） ／ クリックして選択"
        hint="置いた書類は「輸入関税書類」として登録されます。違う時は行の種類を切り替えてください。"
        onFiles={(files) => void handleFiles(files)}
      />
    </>
  );
}

function ImportDocumentRow({
  dealId,
  poId,
  doc,
  nameOf,
}: {
  dealId: string;
  poId: string;
  doc: PurchaseOrderImportDocument;
  nameOf: NameOf;
}) {
  const pdfUrl = usePdfDownloadUrl(doc.storagePath);
  const updateType = useUpdateImportDocumentType();
  const remove = useRemoveImportDocument();
  const uploader = nameOf(doc.uploadedBy);

  const handleType = async (type: ImportDocumentType) => {
    try {
      await updateType.mutateAsync({ dealId, poId, documentId: doc.id, type });
      toast.success(`種類を「${IMPORT_DOCUMENT_TYPE_LABEL[type]}」に変更しました`);
    } catch (err) {
      toast.error(`変更に失敗しました: ${(err as Error).message}`);
    }
  };

  const handleRemove = async () => {
    if (!confirm(`「${doc.fileName}」の登録を外しますか？（PDF ファイル自体は保管場所に残ります）`))
      return;
    try {
      await remove.mutateAsync({ dealId, poId, documentId: doc.id });
      toast.success('登録を外しました');
    } catch (err) {
      toast.error(`失敗しました: ${(err as Error).message}`);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] px-2.5 py-2">
      <select
        value={doc.type}
        onChange={(e) => void handleType(e.target.value as ImportDocumentType)}
        disabled={updateType.isPending}
        aria-label="書類の種類"
        className="h-7 px-1.5 text-[11px] rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
      >
        {(Object.keys(IMPORT_DOCUMENT_TYPE_LABEL) as ImportDocumentType[]).map((t) => (
          <option key={t} value={t}>
            {IMPORT_DOCUMENT_TYPE_LABEL[t]}
          </option>
        ))}
      </select>
      <span className="flex-1 min-w-[140px] truncate font-mono text-xs">{doc.fileName}</span>
      <span className="text-[10px] text-[var(--text-muted)]">
        {fmtDay(doc.uploadedAt)}
        {uploader && ` · ${uploader}`}
      </span>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => pdfUrl.data && window.open(pdfUrl.data, '_blank')}
        disabled={!pdfUrl.data}
      >
        <ExternalLink className="size-3" />
        開く
      </Button>
      <Button variant="ghost" size="sm" onClick={handleRemove} disabled={remove.isPending}>
        削除
      </Button>
    </div>
  );
}

// ─────────────────────────────────────────────
// 内部ヘルパ
// ─────────────────────────────────────────────

/** 旧データ (元ファイル名を持たない最終インボイス) 用: `{timestamp}-{safeName}` から表示名を取り出す。 */
function fileNameFromStoragePath(path: string): string {
  const base = path.split('/').pop() ?? '';
  return base.replace(/^\d+-/, '') || base;
}

function fmtDay(v: unknown): string {
  if (!v) return '';
  if (typeof v === 'string') return new Date(v).toLocaleDateString('ja-JP');
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '';
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const data = reader.result as string;
      // dataURL の "data:application/pdf;base64," 部分を除去
      const idx = data.indexOf(',');
      resolve(idx >= 0 ? data.slice(idx + 1) : data);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// ─────────────────────────────────────────────
// 送金依頼追加の結果トースト (経理担当への通知の送信結果)
// ─────────────────────────────────────────────

const ADD_OUTCOME_LABEL: Record<AccountingChannelOutcome, string> = {
  sent: '送信済み',
  failed: '送信失敗',
  no_target: '送信先が未設定',
  off: 'オフ',
};

function showAddResultToast(n: AccountingNotifyOutcome) {
  const parts: string[] = [];
  if (n.mail !== 'off') parts.push(`メール ${ADD_OUTCOME_LABEL[n.mail]}`);
  if (n.googleChat !== 'off') parts.push(`GC ${ADD_OUTCOME_LABEL[n.googleChat]}`);
  if (n.chatwork !== 'off') parts.push(`CW ${ADD_OUTCOME_LABEL[n.chatwork]}`);
  const description =
    parts.length === 0 ? '経理への通知: オフ（設定なし）' : `経理への通知: ${parts.join(' / ')}`;
  const problem = [n.mail, n.googleChat, n.chatwork].some(
    (o) => o === 'failed' || o === 'no_target',
  );
  if (problem) {
    toast.warning('送金依頼に追加しました（通知の一部が送れていません）', {
      description,
      duration: 10000,
    });
  } else {
    toast.success('送金依頼に追加しました', { description });
  }
}
